import React, { useEffect, useRef } from "react";
import "./FocusTrapping.scss";
interface Props {
  /** The child elements inside the modal */
  children: React.ReactNode;
  /** The ID of the modal container to trap focus in */
  modalId: string;
  /** Determines if focus trapping is active */
  isActive: boolean;
  /** Callback function triggered when the Escape key is pressed */
  onHandleClose?: () => void;
  testId?: string;
}

/**
 * FocusTrapping
 *
 * This component traps keyboard focus within a modal/dialog to ensure accessibility and user experience.
 * It ensures that when users press `Tab` or `Shift + Tab`, the focus stays inside the modal and loops
 * through its focusable elements. It also supports Escape key handling to close the modal.
 *
 * @example
 * ```tsx
 * <FocusTrapping modalId="my-modal-id" isActive={true} onHandleClose={closeFn}>
 *   <div id="my-modal-id">Modal Content</div>
 * </FocusTrapping>
 * ```
 *
 * @param {string} modalId - The `id` of the element to trap focus within.
 * @param {boolean} isActive - Whether the focus trap is active.
 * @param {function} onHandleClose - Optional callback triggered when Escape key is pressed.
 * @param {string} testId - Optional test id for testing utilities.
 *
 * ---
 * 👇 Why we use MutationObserver:
 *
 * In many real-world cases (like forms inside modals), buttons or inputs are initially disabled
 * and only become active after user interaction (e.g., filling out a form). When these buttons
 * become enabled, they should be focusable via the Tab key.
 *
 * If we only detect focusable elements once on mount, we miss those dynamic updates.
 *
 * That’s why we use `MutationObserver`: it watches for changes inside the modal —
 * such as enabling/disabling buttons, or dynamically adding/removing form fields —
 * and updates the list of focusable elements accordingly, **without needing to re-render**.
 *
 * ✅ This ensures focus trapping always works accurately, even as the modal content changes.
 */

const FocusTrapping: React.FC<Props> = ({
  children,
  modalId,
  isActive,
  onHandleClose,
  testId,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const tabbableSelector = `
    a[href]:not([tabindex='-1']),
    area[href]:not([tabindex='-1']),
    input:not([disabled]):not([tabindex='-1']),
    select:not([disabled]):not([tabindex='-1']),
    textarea:not([disabled]):not([tabindex='-1']),
    button:not([disabled]):not([tabindex='-1']),
    iframe:not([tabindex='-1']),
    object:not([tabindex='-1']),
    embed:not([tabindex='-1']),
    [tabindex]:not([tabindex='-1']),
    [contenteditable="true"]:not([tabindex='-1']),
    div.modal-body
  `;

  const isScrollable = (element: HTMLElement) => {
    const hasScrollableContent =
      element.scrollHeight > element.clientHeight ||
      element.scrollWidth > element.clientWidth;
    const overflowYStyle = window.getComputedStyle(element).overflowY;
    const isOverflowScroll =
      overflowYStyle === "auto" || overflowYStyle === "scroll";
    return hasScrollableContent && isOverflowScroll;
  };

  useEffect(() => {
    const modalElement = document.getElementById(modalId);
    if (!modalElement) return () => {};
    if (!isActive) return () => {};
    const timer = setTimeout(() => {
      let focusables: HTMLElement[] = [];

      const updateFocusableElements = () => {
        const nodeList = modalElement.querySelectorAll(tabbableSelector);
        focusables = Array.from(nodeList) as HTMLElement[];
      };

      // Initialize list
      updateFocusableElements();

      const carouselTag = modalElement.querySelector(
        "div#nuka-carousel-slider-frame"
      ) as HTMLElement;

      const firstEl = () => focusables[0];
      const lastEl = () => focusables[focusables.length - 1];
      const focusFirstEl = () => {
        firstEl().setAttribute("tabindex", "0");
        firstEl().classList.add("suppress-outline");
        firstEl().classList.add("firstChild");
        firstEl().focus({ preventScroll: true });
        focusables.forEach((a, i) => {
          if (i !== 0) {
            // cleanup the elements for inaccurate tagging as firstChild
            a.classList.remove("suppress-outline");
            a.classList.remove("firstChild");
          }
          if (a === carouselTag) {
            // disable tabbable for nuka carousel tag
            a.setAttribute("tabindex", "-1");
          }
        });
      };

      if (firstEl()) {
        // special case for mydata-toast
        if (modalId === "mydata-toast") {
          const activeEl = document.activeElement as HTMLElement;
          const isActiveElementChildOfToast = focusables.some(
            (a) => a === activeEl
          );
          if (isActiveElementChildOfToast) return () => {};
        }
        focusFirstEl();
      }

      const lastRegion = modalElement.querySelector("#last-region");
      const handleKeyDown = (event: KeyboardEvent) => {
        const activeEl = document.activeElement as HTMLElement;

        if (event.key === "Escape" && onHandleClose) {
          event.preventDefault();
          onHandleClose();
          return;
        }

        if (
          (event.key === "ArrowDown" || event.key === "ArrowUp") &&
          !isScrollable(activeEl)
        ) {
          const fallback = modalElement.querySelector("div.modal-body");
          if (fallback) {
            (fallback as HTMLElement).focus();
            event.preventDefault();
          }
          return;
        }

        if (
          carouselTag &&
          (event.key === "ArrowLeft" || event.key === "ArrowRight") &&
          activeEl !== carouselTag
        ) {
          event.preventDefault();
          carouselTag.focus();
          return;
        }

        if (event.key === "Tab") {
          const index = focusables.indexOf(activeEl);
          const first = firstEl();
          const last = lastEl();

          let nextIndex = event.shiftKey ? index - 1 : index + 1;
          let nextElement = focusables[nextIndex];

          if (
            activeEl === first &&
            first.classList.contains("suppress-outline")
          ) {
            event.preventDefault();
            first.classList.remove("suppress-outline");
            return;
          }
          // skip modal-body
          if (nextElement?.classList.contains("modal-body")) {
            nextIndex = event.shiftKey ? nextIndex - 1 : nextIndex + 1;
            nextElement = focusables[nextIndex];

            if (nextElement) {
              event.preventDefault();
              setTimeout(() => {
                return nextElement.focus();
              }, 0);
            }
          }

          const lastRegionActiveClass =
            lastRegion?.classList[lastRegion.classList.length - 1];
          const isLastRegion =
            activeEl?.firstChild === lastRegion?.querySelector("div");
          // Special handling for hidden or inactive last-region
          if (lastRegionActiveClass !== "active" && isLastRegion) {
            event.preventDefault();
            first.focus();
            first.classList.remove("firstChild");
            return;
          }

          // Handle edge case where last element is disabled
          if (
            index === -1 ||
            (last.hasAttribute("disabled") && index === focusables.length - 2)
          ) {
            event.preventDefault();
            first.focus();
            first.classList.remove("firstChild");
            return;
          }

          if (event.shiftKey && activeEl === first) {
            event.preventDefault();
            last.focus();
            last.classList.remove("firstChild");
          } else if (!event.shiftKey && activeEl === last) {
            event.preventDefault();
            first.focus();
            first.classList.remove("firstChild");
          }
        }
      };

      // Observe DOM changes inside modal
      const observer = new MutationObserver(() => {
        updateFocusableElements();
      });

      observer.observe(modalElement, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["disabled", "tabindex", "hidden", "style"],
      });

      modalElement.addEventListener("keydown", handleKeyDown, true);

      return () => {
        modalElement.removeEventListener("keydown", handleKeyDown, true);
        observer.disconnect();
      };
    }, 0);

    return () => clearTimeout(timer);
  }, [isActive, modalId, onHandleClose]);

  return (
    <div data-testid={testId} ref={containerRef}>
      {children}
    </div>
  );
};

export default FocusTrapping;
